import { Check } from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import type { ShippingTracker } from '@/types/socialDeal';

export type TrackTimelineProps = {
  stages: ShippingTracker['stages'];
  testID?: string;
};

/**
 * The Track package page's timeline (Figma "Track"): one row per stage, a
 * green check once passed (Delivered included), the current stage's number ringed in green, the
 * rest grey, joined by a line that turns green as the package moves. Labels
 * and times come from the server; a stage Voltr hasn't stamped shows no time.
 */
export function TrackTimeline({ stages, testID }: TrackTimelineProps) {
  const last = stages.length - 1;
  return (
    <View style={styles.card} testID={testID}>
      {stages.map((stage, i) => {
        // Delivered (the last stage) is finished, not in progress: it gets the check too.
        const passed = stage.reached && (!stage.current || i === last);
        const time = stage.reached && stage.at ? formatStageTime(stage.at) : '';
        return (
          <View
            key={stage.label}
            style={styles.row}
            accessible
            accessibilityLabel={[stage.label, stageState(stage, passed), time].filter(Boolean).join(', ')}
            testID={testID ? `${testID}-stage-${i}` : undefined}
          >
            <View style={styles.rail}>
              <View style={[styles.bullet, passed && styles.bulletPassed, stage.current && !passed && styles.bulletCurrent]}>
                {passed ? (
                  <Check size={16} color={colors.frost} strokeWidth={3} />
                ) : (
                  <Text variant="captionBold" color={stage.current ? 'deepGreen' : 'textMuted'}>
                    {String(i + 1)}
                  </Text>
                )}
              </View>
              {i < last ? <View style={[styles.line, stages[i + 1]?.reached && styles.lineReached]} /> : null}
            </View>
            <View style={[styles.text, i === last && styles.textLast]}>
              <Text variant="bodyBold" color={stage.reached ? 'deepGreen' : 'textMuted'}>
                {stage.label}
              </Text>
              {time ? (
                <Text variant="caption" color="textMuted" testID={testID ? `${testID}-time-${i}` : undefined}>
                  {time}
                </Text>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
}

/** What a screen reader says for the bullet the eye reads from its colour. */
function stageState(stage: ShippingTracker['stages'][number], passed: boolean): string {
  if (passed) return 'done';
  return stage.current ? 'current' : 'not yet';
}

/** "Oct 14", in the phone's own time zone; '' for a time that won't parse. */
export function formatStageDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Oct 14 · 2:40 PM", in the phone's own time zone; '' for a time that won't parse. */
export function formatStageTime(iso: string): string {
  const day = formatStageDay(iso);
  if (!day) return '';
  return `${day} · ${new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

const BULLET = 28;
const LINE_WIDTH = 2;

const styles = StyleSheet.create({
  card: {
    padding: spacing.base,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  rail: {
    alignItems: 'center',
  },
  bullet: {
    width: BULLET,
    height: BULLET,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: LINE_WIDTH,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  bulletPassed: {
    borderColor: colors.electricGreen,
    backgroundColor: colors.electricGreen,
  },
  bulletCurrent: {
    borderColor: colors.electricGreen,
    backgroundColor: colors.brightGreenSoft,
  },
  // Fills the gap down to the next bullet; the row's own height sets its length.
  line: {
    flex: 1,
    width: LINE_WIDTH,
    minHeight: spacing.base,
    backgroundColor: colors.border,
  },
  lineReached: {
    backgroundColor: colors.electricGreen,
  },
  text: {
    flex: 1,
    gap: 2,
    paddingTop: spacing.xs,
    paddingBottom: spacing.base,
  },
  // The gap under a row spaces it from the next; the last one ends at the card's padding.
  textLast: {
    paddingBottom: 0,
  },
});
